import { useState } from 'react';

import {
  closeOrderDetailDialog,
  setReplaceMultipleItems,
  setReplaceSingleItem,
  sfWithoutSuggestionsModalObserver,
} from 'features/order/substitution-flow/infra';
import {
  validateFlowMainStep,
  validateItemsOutStock,
} from 'features/order/substitution-flow/domain';
import {
  invalidateGetOrderDetailById,
  useGetOrderDetailById,
} from 'shared/hooks';
import { useSFMainContentLang } from './useSFMainContentLang';

import {
  ATTRIBUTE_NAME_OP_ID,
  NODE_IDS_INIT,
} from 'features/order/substitution-flow/domain';

import type {
  ItemsReplacementModel,
  ProductReplacement,
  SelectSuggestionsParams,
  ToppingReplacement,
} from 'features/order/substitution-flow/domain';
import type { ChangeEvent } from 'react';
import type { OrderId, OrderProductId } from 'shared/domain';

const SUGGESTIONS_INIT: {
  products: [ProductReplacement | undefined, ProductReplacement | undefined];
  toppings: [ToppingReplacement | undefined, ToppingReplacement | undefined];
} = {
  products: [undefined, undefined],
  toppings: [undefined, undefined],
};

export function useSFMainContent(props: Props) {
  const { orderId, fromChatBtn } = props;

  const [selectedNodeIds, setSelectedNodeIds] = useState(NODE_IDS_INIT);
  const [selectedSuggestions, setSelectedSuggestions] = useState(
    SUGGESTIONS_INIT,
  );

  const { data: order } = useGetOrderDetailById(orderId);

  const itemsOutStock = validateItemsOutStock({ order, selectedNodeIds });
  const flowStep = validateFlowMainStep({ items: itemsOutStock });
  const showCTA = flowStep.isMenuItemFinder || flowStep.isMultipleChoice;

  const lang = useSFMainContentLang();

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = event.target;
    if (value === 'topping') {
      const op = event.target.closest(`article[${ATTRIBUTE_NAME_OP_ID}]`);
      const orderProductId = parseInt(
        op?.getAttribute(ATTRIBUTE_NAME_OP_ID) ?? '',
      ) as OrderProductId;
      setSelectedNodeIds(ps => ({
        ...ps,
        toppings: checked
          ? [...ps.toppings, { orderProductId, toppingNodeId: parseInt(name) }]
          : ps.toppings.filter(item => item.toppingNodeId !== parseInt(name)),
      }));
    } else {
      setSelectedNodeIds(ps => ({
        ...ps,
        products: checked
          ? [...ps.products, parseInt(name)]
          : ps.products.filter(opId => opId !== parseInt(name)),
      }));
    }
  };

  const selectSuggestions = (params: SelectSuggestionsParams) => {
    const { index, isProduct, product, topping } = params;
    setSelectedSuggestions(ps => {
      const newState = { ...ps };
      if (isProduct) newState.products[index] = product;
      else newState.toppings[index] = topping;
      return newState;
    });
  };

  const closeWithRefresh = () => {
    setTimeout(() => {
      invalidateGetOrderDetailById(orderId);
      closeOrderDetailDialog();
    }, 100);
  };

  const handleCTA = () => {
    if (flowStep.isMenuItemFinder) {
      const suggestions = {
        products: selectedSuggestions.products.filter(s => s !== undefined),
        toppings: selectedSuggestions.toppings.filter(s => s !== undefined),
      } as ItemsReplacementModel;
      const isAnProduct = suggestions.products.length > 0;
      if (!isAnProduct && suggestions.toppings.length === 0) {
        sfWithoutSuggestionsModalObserver().dispatch({
          order,
          itemsOutStock,
          fromChatBtn,
        });
        closeWithRefresh();
        return;
      }
      setReplaceSingleItem({
        order,
        itemsOutStock,
        suggestions,
        fromChatBtn,
      }).then(closeWithRefresh);
    } else if (flowStep.isMultipleChoice) {
      setReplaceMultipleItems({ order, itemsOutStock, fromChatBtn }).then(
        closeWithRefresh,
      );
    }
  };

  return {
    lang,
    order,
    showCTA,
    flowStep,
    itemsOutStock,
    handleOnChange,
    handleCTA,
    selectSuggestions,
  };
}

interface Props {
  orderId: OrderId;
  fromChatBtn: boolean;
}
